import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HeroSectionContent, CommunityPageSectionRow } from '@/types/cms';
import { Save, X } from 'lucide-react';

interface HeroSectionEditorProps {
  section: CommunityPageSectionRow;
  onSave: (updates: Partial<CommunityPageSectionRow>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const HeroSectionEditor: React.FC<HeroSectionEditorProps> = ({
  section,
  onSave,
  onCancel,
  isLoading = false
}) => {
  const content = section.content as HeroSectionContent;
  
  const [formData, setFormData] = useState({
    title: section.title || '',
    subtitle: content.subtitle || '',
    description: content.description || '',
    cta_text: content.cta_text || '',
    cta_link: content.cta_link || '',
    background_type: content.background_type || 'gradient',
    background_image: content.background_image || '',
    background_video: content.background_video || '',
    background_color: content.background_color || ''
  });

  useEffect(() => {
    setFormData({
      title: section.title || '',
      subtitle: content.subtitle || '',
      description: content.description || '',
      cta_text: content.cta_text || '',
      cta_link: content.cta_link || '',
      background_type: content.background_type || 'gradient',
      background_image: content.background_image || '',
      background_video: content.background_video || '',
      background_color: content.background_color || ''
    });
  }, [section, content]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const { title, ...contentFields } = formData;
    onSave({
      title: title,
      content: {
        ...content,
        ...contentFields
      }
    });
  };

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">Edit Hero Section</h4>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={isLoading}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
          <Button variant="outline" size="sm" onClick={onCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="hero-title">Title</Label>
          <Input
            id="hero-title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Enter hero title"
          />
        </div>

        {/* Subtitle */}
        <div className="space-y-2">
          <Label htmlFor="hero-subtitle">Subtitle (optional)</Label>
          <Input
            id="hero-subtitle"
            value={formData.subtitle}
            onChange={(e) => handleInputChange('subtitle', e.target.value)}
            placeholder="Enter subtitle (leave empty to hide)"
          />
          <p className="text-xs text-muted-foreground">Optional - leave empty to hide this field</p>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="hero-description">Description (optional)</Label>
          <Textarea
            id="hero-description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Enter description (leave empty to hide)"
            rows={3}
          />
          <p className="text-xs text-muted-foreground">Optional - leave empty to hide this field</p>
        </div>

        {/* CTA Button */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="hero-cta-text">CTA Button Text</Label>
            <Input
              id="hero-cta-text"
              value={formData.cta_text}
              onChange={(e) => handleInputChange('cta_text', e.target.value)}
              placeholder="e.g., Get Started"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hero-cta-link">CTA Button Link</Label>
            <Input
              id="hero-cta-link"
              value={formData.cta_link}
              onChange={(e) => handleInputChange('cta_link', e.target.value)}
              placeholder="e.g., /services"
            />
          </div>
        </div>

        {/* Background Settings */}
        <div className="space-y-4 pt-4 border-t">
          <Label>Background Settings</Label>
          
          <div className="space-y-2">
            <Label htmlFor="background-type">Background Type</Label>
            <Select
              value={formData.background_type}
              onValueChange={(value) => handleInputChange('background_type', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minimal">Minimal</SelectItem>
                <SelectItem value="gradient">Gradient</SelectItem>
                <SelectItem value="image">Image</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="color">Solid Color</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Conditional background fields */}
          {formData.background_type === 'image' && (
            <div className="space-y-2">
              <Label htmlFor="background-image">Background Image URL</Label>
              <Input
                id="background-image"
                value={formData.background_image}
                onChange={(e) => handleInputChange('background_image', e.target.value)}
                placeholder="Enter image URL"
              />
            </div>
          )}

          {formData.background_type === 'video' && (
            <div className="space-y-2">
              <Label htmlFor="background-video">Background Video URL</Label>
              <Input
                id="background-video"
                value={formData.background_video}
                onChange={(e) => handleInputChange('background_video', e.target.value)}
                placeholder="Enter video URL"
              />
            </div>
          )}

          {formData.background_type === 'color' && (
            <div className="space-y-2">
              <Label htmlFor="background-color">Background Color</Label>
              <div className="flex gap-2">
                <Input
                  id="background-color"
                  value={formData.background_color}
                  onChange={(e) => handleInputChange('background_color', e.target.value)}
                  placeholder="e.g., #ffffff"
                />
                <input
                  type="color"
                  value={formData.background_color || '#ffffff'}
                  onChange={(e) => handleInputChange('background_color', e.target.value)}
                  className="w-12 h-10 rounded border border-input"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroSectionEditor;